import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Pencil, Check, Calendar } from 'lucide-react'
import api from '../../core/services/api'
import { Toast, ConfirmModal } from '../Toast'
import { Scadenza, ScadenzaForm } from '../../types/strumenti'

interface ScadenzeModalProps {
  isOpen: boolean
  onClose: () => void
  strumentoId: string
  strumentoDescrizione: string
}

const emptyForm: ScadenzaForm = {
  descrizione: '',
  data_scadenza: '',
  giorni_preavviso: 30,
  risolta: false
}

export function ScadenzeModal({
  isOpen,
  onClose,
  strumentoId,
  strumentoDescrizione
}: ScadenzeModalProps) {
  const [scadenze, setScadenze] = useState<Scadenza[]>([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState<ScadenzaForm>({ ...emptyForm })
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<ScadenzaForm>({ ...emptyForm })
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadScadenze()
      setForm({ ...emptyForm })
      setEditingId(null)
    }
  }, [isOpen, strumentoId])

  const loadScadenze = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/strumenti/${strumentoId}/scadenze`)
      setScadenze(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento scadenze', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    if (!form.descrizione.trim() || !form.data_scadenza) {
      setToast({ message: 'Compila descrizione e data scadenza', type: 'error' })
      return
    }

    try {
      setSaving(true)
      await api.post(`/v1/strumenti/${strumentoId}/scadenze`, {
        descrizione: form.descrizione.trim(),
        data_scadenza: form.data_scadenza,
        giorni_preavviso: form.giorni_preavviso,
        risolta: false
      })
      setToast({ message: 'Scadenza aggiunta', type: 'success' })
      setForm({ ...emptyForm })
      loadScadenze()
    } catch {
      setToast({ message: 'Errore nel salvataggio', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleRisolta = async (scadenza: Scadenza) => {
    try {
      await api.put(`/v1/strumenti/${strumentoId}/scadenze/${scadenza.id}`, {
        risolta: !scadenza.risolta
      })
      loadScadenze()
    } catch {
      setToast({ message: 'Errore nell\'aggiornamento', type: 'error' })
    }
  }

  const startEdit = (scadenza: Scadenza) => {
    setEditingId(scadenza.id)
    setEditForm({
      descrizione: scadenza.descrizione,
      data_scadenza: scadenza.data_scadenza,
      giorni_preavviso: scadenza.giorni_preavviso,
      risolta: scadenza.risolta
    })
  }

  const handleSaveEdit = async () => {
    if (!editingId || !editForm.descrizione.trim() || !editForm.data_scadenza) return

    try {
      await api.put(`/v1/strumenti/${strumentoId}/scadenze/${editingId}`, {
        descrizione: editForm.descrizione.trim(),
        data_scadenza: editForm.data_scadenza,
        giorni_preavviso: editForm.giorni_preavviso
      })
      setToast({ message: 'Scadenza aggiornata', type: 'success' })
      setEditingId(null)
      loadScadenze()
    } catch {
      setToast({ message: 'Errore nell\'aggiornamento', type: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.delete(`/v1/strumenti/${strumentoId}/scadenze/${deleteTarget}`)
      setToast({ message: 'Scadenza eliminata', type: 'success' })
      setDeleteTarget(null)
      loadScadenze()
    } catch {
      setToast({ message: 'Errore nell\'eliminazione', type: 'error' })
      setDeleteTarget(null)
    }
  }

  const getScadenzaColor = (scadenza: Scadenza) => {
    if (scadenza.risolta) return 'border-green-200 bg-green-50'
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dataScadenza = new Date(scadenza.data_scadenza)
    const daysRemaining = Math.ceil((dataScadenza.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (daysRemaining <= 0) return 'border-red-200 bg-red-50'
    if (daysRemaining <= scadenza.giorni_preavviso) return 'border-amber-200 bg-amber-50'
    return 'border-gray-200 bg-white'
  }

  const getStatusLabel = (scadenza: Scadenza) => {
    if (scadenza.risolta) return { text: 'Risolta', color: 'text-green-600' }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dataScadenza = new Date(scadenza.data_scadenza)
    const daysRemaining = Math.ceil((dataScadenza.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (daysRemaining <= 0) return { text: 'Scaduta', color: 'text-red-600' }
    if (daysRemaining <= scadenza.giorni_preavviso) return { text: `Scade tra ${daysRemaining}g`, color: 'text-amber-600' }
    return { text: `${daysRemaining}g rimanenti`, color: 'text-gray-500' }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    })
  }

  if (!isOpen) return null

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Elimina Scadenza"
        message="Sei sicuro di voler eliminare questa scadenza?"
        confirmText="Elimina"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-600" />
                Scadenze
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">{strumentoDescrizione}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Add Form */}
            <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nuova Scadenza
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Descrizione *</label>
                  <input
                    type="text"
                    value={form.descrizione}
                    onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                    placeholder="es. Revisione annuale"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Data Scadenza *</label>
                    <input
                      type="date"
                      value={form.data_scadenza}
                      onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Giorni Preavviso</label>
                    <input
                      type="number"
                      value={form.giorni_preavviso}
                      onChange={(e) => setForm({ ...form, giorni_preavviso: parseInt(e.target.value) || 30 })}
                      min={1}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleAdd}
                  disabled={saving || !form.descrizione.trim() || !form.data_scadenza}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <Plus className="w-4 h-4" />
                  {saving ? 'Salvataggio...' : 'Aggiungi'}
                </button>
              </div>
            </div>

            {/* Lista Scadenze */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Caricamento...</div>
            ) : scadenze.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessuna scadenza presente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {scadenze.map((scadenza) => {
                  const statusLabel = getStatusLabel(scadenza)
                  const isEditing = editingId === scadenza.id

                  return (
                    <div
                      key={scadenza.id}
                      className={`border rounded-lg p-3 transition-colors ${getScadenzaColor(scadenza)}`}
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editForm.descrizione}
                            onChange={(e) => setEditForm({ ...editForm, descrizione: e.target.value })}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="date"
                              value={editForm.data_scadenza}
                              onChange={(e) => setEditForm({ ...editForm, data_scadenza: e.target.value })}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <input
                              type="number"
                              value={editForm.giorni_preavviso}
                              onChange={(e) => setEditForm({ ...editForm, giorni_preavviso: parseInt(e.target.value) || 30 })}
                              min={1}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Giorni preavviso"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveEdit}
                              className="px-3 py-1 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
                            >
                              Salva
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                            >
                              Annulla
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          {/* Toggle Risolta */}
                          <button
                            onClick={() => handleToggleRisolta(scadenza)}
                            className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              scadenza.risolta
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 hover:border-primary-400'
                            }`}
                            title={scadenza.risolta ? 'Segna come non risolta' : 'Segna come risolta'}
                          >
                            {scadenza.risolta && <Check className="w-3.5 h-3.5" />}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-sm ${scadenza.risolta ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                              {scadenza.descrizione}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                              <span>{formatDate(scadenza.data_scadenza)}</span>
                              <span>-</span>
                              <span>Preavviso: {scadenza.giorni_preavviso}g</span>
                              <span>-</span>
                              <span className={`font-medium ${statusLabel.color}`}>
                                {statusLabel.text}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => startEdit(scadenza)}
                              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                              title="Modifica"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(scadenza.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                              title="Elimina"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
